import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MaterialDialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIconMui from '@material-ui/icons/Close';
import clsx from 'clsx';

import { CloseIcon, colors } from '../../assets';
import { disablePageScroll, enablePageScrol } from '../../utils';
import { Button } from '../button/Button';
import { DashedSeparator } from '../dashedSeparator/DashedSeparator';
import { useStyles } from './Dialog.styles';
import { DialogProps } from './Dialog.types';
import { classesMap } from './Dialog.utils';
import { FullScreenDialogHeader } from './fullScreenDialogHeader/FullScreenDialogHeader';

export const Dialog = memo(
  ({
    open,
    title,
    variant = 'basic',
    customHeader,
    maxWidth = 'sm',
    fullScreen,
    fullScreenProps,
    hideCloseButton,
    disablePortal = false,
    disableBackdropClick = false,
    disableEscapeKeyDown = false,
    disablePageScrollChange = false,
    muiDialogClasses,
    classes,
    children,
    onClose,
    ...props
  }: DialogProps) => {
    const [isShowScrollToTop, setIsShowScrollToTop] = useState(false);

    const styles = useStyles({
      fullScreenBackgroundColor: fullScreenProps?.backgroundColor
        ? fullScreenProps.backgroundColor
        : colors.basic.white,
      isShowScrollToTop: isShowScrollToTop,
    });

    const innerRef = useRef<HTMLDivElement>(null);
    const scrollTimeoutRef = useRef<number | null>(null);

    const dialogClasses = useMemo(() => {
      return classesMap(styles)[variant];
    }, [styles, variant]);

    const handleClose = (_: Record<string, never>, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (onClose) {
        if (disableBackdropClick && reason === 'backdropClick') {
          return;
        }

        if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
          return;
        }

        onClose();
      }
    };

    const handleScrollToTop = useCallback(() => {
      if (variant === 'fullScreen' && innerRef.current) {
        innerRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, [variant]);

    const handleScroll = useCallback(() => {
      if (scrollTimeoutRef.current !== null) {
        cancelAnimationFrame(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = requestAnimationFrame(() => {
        if (variant === 'fullScreen' && fullScreenProps?.isChangeBackToTop && innerRef.current) {
          if (innerRef.current.scrollTop >= 100 && !isShowScrollToTop) {
            setIsShowScrollToTop(true);
          } else if (innerRef.current.scrollTop < 100 && isShowScrollToTop) {
            setIsShowScrollToTop(false);
          }
        }
      });
    }, [variant, isShowScrollToTop, fullScreenProps?.isChangeBackToTop]);

    useEffect(() => {
      if (scrollTimeoutRef.current !== null) {
        cancelAnimationFrame(scrollTimeoutRef.current);
      }
    }, [handleScroll]);

    useEffect(() => {
      if (disablePageScrollChange) {
        return;
      }

      if (open) {
        disablePageScroll();
      } else {
        enablePageScrol();
      }
    }, [open, disablePageScrollChange]);

    return (
      <MaterialDialog
        open={open}
        maxWidth={variant === 'fullScreen' ? false : maxWidth}
        fullScreen={variant === 'fullScreen' || fullScreen}
        scroll={['estimate', 'custom'].includes(variant) ? 'body' : undefined}
        disablePortal={disablePortal}
        data-testid={props['data-testid']}
        classes={{ ...dialogClasses, ...muiDialogClasses }}
        onClose={handleClose}
        {...props}
      >
        <div
          ref={innerRef}
          id={variant === 'fullScreen' ? 'fullScreenDialogContent' : undefined}
          className={clsx(
            classes?.wrapper,
            variant === 'schedule' && styles.scheduleContent,
            variant === 'estimate' && styles.estimateContent,
            variant === 'basic' && styles.basicContent,
            variant === 'withSeparator' && styles.withSeparatorContent,
            variant === 'fullScreen' && styles.fullScreenContent,
          )}
          onScroll={handleScroll}
        >
          {variant === 'schedule' && (
            <>
              {customHeader ??
                (onClose && !hideCloseButton && (
                  <div className={styles.scheduleCloseBtnContainer}>
                    <Button buttonType="twoTone" size="medium" onClick={onClose}>
                      <CloseIconMui className={styles.scheduleCloseIcon} />
                    </Button>
                  </div>
                ))}

              {children}
            </>
          )}

          {variant === 'estimate' && (
            <Grid container wrap="nowrap" direction="column" spacing={3}>
              {customHeader ?? (
                <Grid item>
                  <Grid container wrap="nowrap" spacing={2} alignItems="center">
                    {title && (
                      <Grid item>
                        <Typography variant="h5" color="textSecondary" className={styles.estimateTitle}>
                          {title}
                        </Typography>
                      </Grid>
                    )}

                    {onClose && !hideCloseButton && (
                      <Grid item className={styles.estimateCloseBtnContainer}>
                        <IconButton
                          id="close-modal"
                          color="secondary"
                          onClick={onClose}
                          className={styles.estimateCloseBtn}
                        >
                          <CloseIcon color={colors.secondary.main} fontSize={18} />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              <Grid item>{children}</Grid>
            </Grid>
          )}

          {variant === 'basic' && (
            <Grid container wrap="nowrap" direction="column" className={styles.basicContentInner}>
              {customHeader ?? (
                <Grid item className={styles.basicHeader}>
                  <Grid container wrap="nowrap" spacing={2} className={styles.basicTitleContainer} alignItems="center">
                    {title && (
                      <Grid item>
                        <Typography variant="h4" color="textSecondary">
                          {title}
                        </Typography>
                      </Grid>
                    )}

                    {onClose && !hideCloseButton && (
                      <Grid item className={styles.closeBtnContainer}>
                        <IconButton id="close-modal" onClick={onClose} className={styles.closeBtn}>
                          <CloseIcon fontSize={12} />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              <Grid item className={styles.basicChildrenContainer}>
                {children ?? null}
              </Grid>
            </Grid>
          )}

          {variant === 'withSeparator' && (
            <Grid container wrap="nowrap" direction="column">
              {customHeader ?? (
                <Grid item>
                  <Grid
                    container
                    wrap="nowrap"
                    spacing={2}
                    className={styles.withSeparatorTitleContainer}
                    alignItems="center"
                  >
                    {title && (
                      <Grid item>
                        <Typography variant="h3" color="textSecondary">
                          {title}
                        </Typography>
                      </Grid>
                    )}

                    {onClose && !hideCloseButton && (
                      <Grid item className={styles.closeBtnContainer}>
                        <IconButton id="close-modal" onClick={onClose} className={styles.closeBtn}>
                          <CloseIcon fontSize={12} />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              <Grid className={styles.separatorContainer}>
                <DashedSeparator dashedItemProps={{ width: 4 }} />
              </Grid>

              <Grid item className={styles.withSeparatorChildrenContainer}>
                {React.cloneElement(children as React.ReactElement, { onClose })}
              </Grid>
            </Grid>
          )}

          {variant === 'custom' && children}

          {variant === 'fullScreen' && (
            <>
              {customHeader ?? (
                <FullScreenDialogHeader
                  {...fullScreenProps}
                  hideCloseButton={hideCloseButton}
                  showScrollToTop={isShowScrollToTop}
                  classes={{ root: styles.fullScreenHeader }}
                  onScrollToTop={handleScrollToTop}
                  onClose={onClose}
                />
              )}

              {children}
            </>
          )}
        </div>
      </MaterialDialog>
    );
  },
);
